import styled from "@emotion/styled";
import { getDatabase, ref, child, get, set } from "firebase/database";
import { useEffect, useState } from "react";
import { fireBaseApp } from "../db/db";
import { castVote, increaseVoteCount } from "../utils/utils";
import { Button } from "./Button";
import { FadeInContent } from "./FadeInContent";
import { Italic } from "./Italic";
import { Spinner } from "./Spinner";

const Option = styled.input`
  display: none;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-top: 1px solid #eaeaea;
  padding-top: 20px;
  gap: 10px;
  width: 100%;
  padding-bottom: 20px;
  padding-left: 10px;
  padding-right: 10px;
  @media(max-width: 748px) {
    font-size: 12px;
  }
`;

const OptionLabel = styled.label<{ selected: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  height: 50px;
  width: 100%;
  padding: 15px;
  border-radius: 10px;
  background-color: ${(props) => (props.selected ? "#d1cbc1" : "#eaeaea")};
  opacity: ${(props) => (props.selected ? 1 : 0.5)};
`;

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 10px;
  border-radius: 10px;
  border: 1px solid #eaeaea;
  width: 100%;
  padding: 10px;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const SubDesc = styled.div`
  font-size: 12px;
  opacity: 0.8;
`;

const Total = styled.div`
  font-size: 12px;
  opacity: 0.8;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding-right: 10px;
`

const QuestionText = styled.div`
  font-size: 16px;

  display: flex;
  flex-direction: column;
  margin-top: 10px;
  @media(max-width: 748px) {
    font-size: 13px;
    text-align: start;
  }
`;

const SelectButton = styled(Button)`
  width: 60px;
`;

const SubmitButton = styled(Button)<{ disabled: boolean }>`
  position: relative;
  width: 200px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;

  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  @media (max-width: 748px) {
    width: 150px;
    font-size: 12px;
  }
`;

interface VotingProps {
  vote: number[];
  voted: boolean;
  writeVote: (e: React.FormEvent<HTMLFormElement>) => void;
  currentSelected: number;
  setCurrentSelected: (value: number) => void;
  questionText: string;
  option1Text: string;
  option2Text: string;
}

const VotingArea = ({
  vote,
  voted,
  writeVote,
  currentSelected,
  setCurrentSelected,
  questionText,
  option1Text,
  option2Text,
}: VotingProps) => {
  const [isLoading, setIsLoading] = useState(false);


  return (
    <Form onSubmit={async (e) => {
      e.preventDefault()
      setIsLoading(true)
      await writeVote(e)
      setIsLoading(false)
      }}>
      <QuestionContainer>
        <QuestionText>
          {questionText}
        </QuestionText>

        <Total >- 총 {vote[0] + vote[1]}명 투표 -</Total>

        <OptionLabel
          aria-disabled
          selected={currentSelected === 0}
          htmlFor={"vote1" + questionText}
        >
          <div>{option1Text}</div>

          {voted ? (
            <SubDesc>지금까지 {vote[0]}표</SubDesc>
          ) : (
            <SelectButton
              onClick={() => {
                setCurrentSelected(0);
              }}
            >
              선택
            </SelectButton>
          )}
        </OptionLabel>
        <OptionLabel
          selected={currentSelected === 1}
          htmlFor={"vote2" + questionText}
        >
          <div>{option2Text}</div>

          {voted ? (
            <SubDesc>지금까지 {vote[1]}표</SubDesc>
          ) : (
            <SelectButton
              onClick={() => {
                setCurrentSelected(1);
              }}
            >
              선택
            </SelectButton>
          )}
        </OptionLabel>

        <SubmitButton as="button" type="submit" disabled={voted}>
          {voted ? "투표를 이미 했어요" : "투표하기"}
          {isLoading && <Spinner/>}
        </SubmitButton>
      </QuestionContainer>

      <Option type="radio" name="vote" id={"vote1" + questionText} value={0} />
      <Option type="radio" name="vote" id={"vote2" + questionText} value={1} />
    </Form>
  );
};

const useCommunityVoting = (endPoint: string) => {
  const [vote, setVote] = useState([0, 0]);
  const [currentSelected, setCurrentSelected] = useState(5);
  const [voted, setVoted] = useState(false);
  const checkVotedFromLocalStorage = () => {
    const voted = localStorage.getItem("CMvoted" + endPoint);
    if (voted === "true") {
      setVoted(true);
    }
  };
  useEffect(() => {
    checkVotedFromLocalStorage();
  }, []);

  const setVotedToLocalStorage = async () => {
    localStorage.setItem("CMvoted" + endPoint, "true");
    setVoted(true);
  };
  const getCurrentVote = async () => {
    const snapshot = await get(child(ref(getDatabase(fireBaseApp)), endPoint));
    const voteResult = snapshot.val();
    setVote(voteResult);
    return voteResult;
  };
  useEffect(() => {
    getCurrentVote();
  }, []);
  useEffect(() => {
    if (voted) {
      setCurrentSelected(99999);
    }
  }, [voted]);
  const writeVote = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (voted) return;
    // @ts-ignore
    const vote = e.currentTarget.vote.value;
    const voteResult = await getCurrentVote();

    if (vote == 0) {
      voteResult[0] = voteResult[0] + 1;
    } else {
      voteResult[1] = voteResult[1] + 1;
    }

    // await set(ref(getDatabase(fireBaseApp), endPoint), voteResult);
    // await castVote(endPoint, voteResult)
    await increaseVoteCount(endPoint, vote)

    setVote(voteResult);
    setVotedToLocalStorage();
  };

  return {
    vote,
    writeVote,
    currentSelected,
    setCurrentSelected,
    voted,
  };
};

export const CommunityVoting = () => {
  const { vote, writeVote, currentSelected, setCurrentSelected, voted } =
    useCommunityVoting(`communityVoting/song`);

  const {
    vote: vote2,
    writeVote: writeVote2,
    voted: voted2,
    currentSelected: currentSelected2,
    setCurrentSelected: setCurrentSelected2,
  } = useCommunityVoting(`communityVoting/party`);

  // const {
  //   vote: vote3,
  //   writeVote: writeVote3,
  //   voted: voted3,
  //   currentSelected: currentSelected3,
  //   setCurrentSelected: setCurrentSelected3,
  // } = useCommunityVoting(`communityVoting/special`);

  const {
    vote: vote4,
    writeVote: writeVote4,
    voted: voted4,
    currentSelected: currentSelected4,
    setCurrentSelected: setCurrentSelected4,
  } = useCommunityVoting(`communityVoting/marching`);

  return (
    <Container>
      <FadeInContent
        style={{
          marginBottom: "20px",
        }}
      >
        <Italic>- Guest's Vote -</Italic>
      </FadeInContent>
      {/* <FadeInContent>
        <b>여러분이 함께 만드는 웨딩~~</b>
      </FadeInContent> */}
      <FadeInContent
        style={{
          width: "100%",
        }}
      >
        <VotingArea
          vote={vote}
          voted={voted}
          writeVote={writeVote}
          currentSelected={currentSelected}
          setCurrentSelected={setCurrentSelected}
          questionText="결혼식의 축가는 어떤 걸로 하면 좋을까요?"
          option1Text="눈가가 촉촉해지는 감동적인 발라드"
          option2Text="신랑신부도 같이 어울릴 수 있는 신나는 노래"
        />
      </FadeInContent>
      <FadeInContent
        style={{
          width: "100%",
        }}
      >
        <VotingArea
          vote={vote4}
          voted={voted4}
          writeVote={writeVote4}
          currentSelected={currentSelected4}
          setCurrentSelected={setCurrentSelected4}
          questionText="신랑신부의 행진, 어떤 모습을 보여드릴까요?"
          option1Text="우아하고 품위있게!"
          option2Text="귀여운 율동으로 발랄하게!"
        />
      </FadeInContent>
      {/* <FadeInContent
        style={{
          width: "100%",
        }}
      >
        <VotingArea
          vote={vote3}
          voted={voted3}
          writeVote={writeVote3}
          currentSelected={currentSelected3}
          setCurrentSelected={setCurrentSelected3}
          questionText="하객석에 자만추를 위한 테이블 하나 준비해볼까요?"
          option1Text="YES! 새로운 인연의 탄생이 될 수도..? 좋아요."
          option2Text=" NO! 괜찮습니다…^^"
        />
      </FadeInContent> */}
      <FadeInContent
        style={{
          width: "100%",
        }}
      >
        <VotingArea
          vote={vote2}
          voted={voted2}
          writeVote={writeVote2}
          currentSelected={currentSelected2}
          setCurrentSelected={setCurrentSelected2}
          questionText="식이 끝나고 본격적으로 놀아재끼는 애프터파티의 분위기는?"
          option1Text="클럽이 따로 없다! 신나는 음악과 DJ"
          option2Text="잔잔한 노래와 함께 고품격 대화의 시간"
        />
      </FadeInContent>
    </Container>
  );
};
